import { concat, fn } from '@ember/helper';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { get, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import { task, timeout, dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import RouteTemplate from 'ember-route-template';
import SuperTextEditor from 'ember-super-prosemirror/components/super-text-editor';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, not, or } from 'ember-truth-helpers';
import EmberWormhole from 'ember-wormhole/components/ember-wormhole';
import ConfirmDirtyTransition from 'garaje/components/confirm-dirty-transition';
import SetupGuideSteps from 'garaje/components/setup-guide-steps';
import SlideToggle from 'garaje/components/slide-toggle';
import TrialDaysLeft from 'garaje/components/trial-days-left';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import PageSection from 'garaje/components/ui/page/section';
import UiButton from 'garaje/components/ui-button';
import PreRegRequired from 'garaje/components/visitors/settings/invites/pre-reg-required';
import TouchlessSignIn from 'garaje/components/visitors/settings/invites/touchless-sign-in';
import VisitorReminderSettings, {
  DEFAULT_THRESHOLD_MINUTES,
} from 'garaje/components/visitors/settings/invites/visitor-reminder-settings';
import featureEnabled from 'garaje/helpers/feature-enabled';
import type LocationModel from 'garaje/models/location';
import PropagableSettingsChanged from 'garaje/pods/components/propagable-settings-changed/gts';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ImpressionsService from 'garaje/services/impressions';
import type StateService from 'garaje/services/state';
import type StatsigService from 'garaje/services/statsig';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { isRTLLocale } from 'garaje/utils/locale-options';
import { loadSrcDoc } from 'garaje/utils/script-loader';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import LiquidIf from 'liquid-fire/components/liquid-if';
import { or as orMacro } from 'macro-decorators';

import type { VisitorsSettingsInvitesRouteModel } from './route';

interface VisitorsSettingsInvitesTemplateSignature {
  Args: {
    model: VisitorsSettingsInvitesRouteModel;
  };
}

class VisitorsSettingsInvitesTemplate extends Component<VisitorsSettingsInvitesTemplateSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare impressions: ImpressionsService;
  @service declare state: StateService;
  @service declare statsig: StatsigService;

  @tracked testEmailSent = false;
  @tracked editEmailVisible = true;
  @tracked isSecurityDeskLinkPanelOpen = false;
  @tracked previewEmailObject: { body: string; subject: string } | null = null;
  @tracked securityDeskLinkCallback?: Promise<void>;
  @tracked confirmRegenerateSecurityDeskLink = false;
  @tracked lastMarkdown?: string;

  @orMacro('currentAdmin.isGlobalAdmin', 'currentAdmin.isLocationAdmin') isAdmin!: boolean;

  get showSetupGuide(): boolean {
    return this.isAdmin && this.featureFlags.isEnabled('growth_show_visitors_setup_guide_stepper');
  }

  get canShowAddEmployeesTooltip(): boolean {
    if (
      this.args.model.currentLocation?.preRegistrationEnabled &&
      this.state?._companyMeta?.['employees-count'] === 1 &&
      this.featureFlags.isEnabled('growth_employee_directory_empty_state')
    ) {
      return true;
    }
    return false;
  }

  get hasAnyFlowsWithIdScanningEnabled(): boolean {
    let flows = this.args.model.allFlowsForCurrentLocation;
    flows = flows && flows.length ? flows.filter((flow) => flow.enabled) : [];
    // eslint-disable-next-line ember/no-get
    flows = flows.filter((vt) => get(vt, 'idScanPage.enabled') === true);
    return flows.length > 0;
  }

  get isNearVisitDisabledByReminders(): boolean {
    if (!this.featureFlags.isEnabled('visitors-invite-email-reminders-v2')) return false;
    return this.args.model.currentLocation?.visitorReminderThresholdMinutes !== DEFAULT_THRESHOLD_MINUTES;
  }

  get showPropertyQrCode(): boolean {
    if (!this.args.model.connectLocationConfiguration) return false;

    const { canEnableVisitorPropertyDigitalPass, visitorPropertyDigitalPassEnabled } =
      this.args.model.connectLocationConfiguration;

    return canEnableVisitorPropertyDigitalPass && visitorPropertyDigitalPassEnabled;
  }

  saveLocationTask = dropTask(async (location: LocationModel) => {
    try {
      await location.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      location.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  sendTestEmailTask = task(async () => {
    const locationId = this.args.model.currentLocation!.id;
    const url = urlBuilder.v2.sendInvitePreviewUrl(locationId);
    const promise = this.ajaxFetch.request(url, {
      type: 'POST',
      data: {
        notes: this.args.model.currentLocation!.preRegistrationNotes,
      },
    });
    try {
      await promise;
    } catch (e) {
      // Response is not expected to be valid JSON, so ignore syntax error
      if (!(e instanceof SyntaxError)) {
        const errorText = parseErrorForDisplay(e);
        this.flashMessages.showAndHideFlash('error', errorText);
      }
    }
    this.flashMessages.showAndHideFlash('success', 'Sent!');
    this.testEmailSent = true;
    await timeout(zft(2500));
    this.testEmailSent = false;
  });

  togglePreRegistration = dropTask(async (shouldEnablePreRegistration: boolean) => {
    const { currentLocation } = this.args.model;

    if (shouldEnablePreRegistration) {
      currentLocation!.preRegistrationEnabled = true;
    } else {
      currentLocation!.preRegistrationEnabled = false;
      if (currentLocation!.preRegistrationRequiredEnabled) {
        currentLocation!.preRegistrationRequiredEnabled = false;
      }
    }

    await this.saveLocationTask.perform(currentLocation!);
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_INVITES_PRE_REGISTRATION[shouldEnablePreRegistration ? 'ENABLED' : 'DISABLED'],
    );
  });

  togglePreRegistrationRequiredEnabled = dropTask(async (value: boolean) => {
    this.args.model.currentLocation!.preRegistrationRequiredEnabled = value;
    await this.saveLocationTask.perform(this.args.model.currentLocation!);
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_INVITES_PRE_REGISTRATION_REQUIRED[value ? 'ENABLED' : 'DISABLED'],
    );
  });

  disableSecurityDeskLink = task(async () => {
    try {
      const { currentLocation } = this.args.model;
      currentLocation!.securityDeskLinkEnabled = false;
      await currentLocation!.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      await this.impressions.postImpression.perform(IMPRESSION_NAMES.VR_INVITES_SECURITY_DESK_LINK['DISABLED']);
    } catch (_e) {
      this.flashMessages.showAndHideFlash('warning', 'Error saving!');
    }
  });

  enableSecurityDeskLink = task(async () => {
    try {
      const { currentLocation } = this.args.model;
      currentLocation!.securityDeskLinkEnabled = true;
      await currentLocation!.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      await this.impressions.postImpression.perform(IMPRESSION_NAMES.VR_INVITES_SECURITY_DESK_LINK['ENABLED']);
    } catch (_e) {
      this.flashMessages.showAndHideFlash('warning', 'Error saving!');
    }
  });

  toggleNearVisitScreeningEnabled = dropTask(async (value: boolean) => {
    this.args.model.currentLocation!.nearVisitScreeningEnabled = value;
    await this.saveLocationTask.perform(this.args.model.currentLocation!);
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_INVITES_NEAR_VISIT_REGISTRATION[value ? 'ENABLED' : 'DISABLED'],
    );
  });

  toggleCCReceptionistEnabled = dropTask(async (value: boolean) => {
    this.args.model.currentLocation!.ccReceptionistEnabled = value;
    await this.saveLocationTask.perform(this.args.model.currentLocation!);
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_INVITES_INVITE_NOTIFICATION[value ? 'ENABLED' : 'DISABLED'],
    );
  });

  loadSrcDoc(): Promise<void> {
    return loadSrcDoc().then((didLoad) => {
      if (didLoad) {
        const iframe = document.getElementsByClassName('preRegistrationIframe')[0];

        if (iframe) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          set(window.srcDoc, iframe, this.previewEmailObject.body);
        }
      }
    });
  }

  trackTooltipShown = (): void => {
    this.statsig.logEvent('employee-invite-tooltip-seen');
  };

  trackTooltipLinkClick = (): void => {
    this.statsig.logEvent('employee-invite-tooltip-directory-clicked');
  };

  regenerateSecurityDeskLink = (): void => {
    this.securityDeskLinkCallback = this.args.model.currentLocation!.generateSecurityDeskLink();
  };

  preRegistrationNotesUpdated = (notes: string): void => {
    this.args.model.currentLocation!.preRegistrationNotes = notes;
  };

  getEmailPreview = (): void => {
    const preRegistrationNotes = this.args.model.currentLocation!.preRegistrationNotes;

    if (this.lastMarkdown !== preRegistrationNotes) {
      this.lastMarkdown = preRegistrationNotes;
      this.previewEmailObject = null;
    }

    const locationId = this.args.model.currentLocation!.id;

    void this.ajaxFetch
      .request<this['previewEmailObject']>(urlBuilder.v2.invitePreviewUrl(locationId), {
        type: 'POST',
        data: { notes: preRegistrationNotes },
      })
      .then((response) => {
        this.previewEmailObject = response;
      })
      .finally(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line ember/no-runloop
        scheduleOnce('afterRender', this, 'loadSrcDoc');
      });
  };

  <template>
    {{#if this.showSetupGuide}}
      <EmberWormhole @to="onboarding">
        <SetupGuideSteps />
      </EmberWormhole>
    {{/if}}

    <UiPage>
      <UiHeader>
        Invites
        <TrialDaysLeft @subscription={{@model.vrSubscription}} />
        <PropagableSettingsChanged />
      </UiHeader>

      <PageSection>
        {{#if (can "enable invites")}}
          <SettingsPanel as |panel|>
            <panel.header
              @iconUri="/assets/images/features/prereg.svg"
              @title="Visitor invites"
              @description="Allow employees and admins to invite visitors so they can pre-register before their arrival."
              as |header|
            >
              <header.controls>
                <label class="toggle">
                  <input
                    checked={{@model.currentLocation.preRegistrationEnabled}}
                    data-test-pre-registration-toggle
                    disabled={{this.saveLocationTask.isRunning}}
                    {{on "change" (perform this.togglePreRegistration value="target.checked")}}
                    type="checkbox"
                  />
                  <span></span>
                </label>
              </header.controls>
            </panel.header>
            {{#if this.canShowAddEmployeesTooltip}}
              <MessageBox
                @type="attention"
                @header="Add employees to maximize visitor invites"
                class="my-2 ml-16 w-[570px]"
                {{didInsert this.trackTooltipShown}}
              >
                <p class="mb-0">
                  Add more employees to the
                  <LinkTo
                    data-test-link-to-email-templates
                    @route="employees.directory"
                    {{on "click" this.trackTooltipLinkClick}}
                  >
                    employee directory
                  </LinkTo>
                  so they can invite guests.
                </p>
              </MessageBox>
            {{/if}}
          </SettingsPanel>
        {{/if}}

        {{! FF: pre-reg-required !}}
        <LiquidIf
          @predicate={{and @model.currentLocation.preRegistrationEnabled (can "enable pre-registration-required")}}
          class="animate-if-vertical"
        >
          {{#if (featureEnabled "host-response-action-unknown-visitor")}}
            {{#if @model.currentLocation}}
              <PreRegRequired @location={{@model.currentLocation}} @saveTask={{this.saveLocationTask}} />
            {{/if}}
          {{else}}
            <SettingsPanel as |panel|>
              <panel.header
                @iconUri="/assets/images/features/prereg-invites-required.svg"
                @title="Require pre-registration for sign-in"
                @description="Require admin approval when a visitor that is not pre-registered signs in."
                as |header|
              >
                <header.controls>
                  <label class="toggle">
                    <input
                      data-test-pre-reg-required-toggle
                      checked={{@model.currentLocation.preRegistrationRequiredEnabled}}
                      disabled={{this.saveLocationTask.isRunning}}
                      type="checkbox"
                      {{on "change" (perform this.togglePreRegistrationRequiredEnabled value="target.checked")}}
                    />
                    <span></span>
                  </label>
                </header.controls>
              </panel.header>
            </SettingsPanel>
          {{/if}}
        </LiquidIf>

        {{#if (can "enable touchless-sign-in")}}
          {{#if @model.currentLocation}}
            <TouchlessSignIn
              @location={{@model.currentLocation}}
              @saveTask={{this.saveLocationTask}}
              @showWarning={{this.hasAnyFlowsWithIdScanningEnabled}}
              @warningHeader="ID scanning not supported"
              @warningMessage="Visitors will not be asked to scan their ID when touchless sign-in is enabled."
            />
          {{/if}}
        {{/if}}

        {{#if
          (and (featureEnabled "visitors-invite-email-reminders-v2") (can "update-visitor-reminders notification"))
        }}
          {{#if @model.currentLocation}}
            <VisitorReminderSettings @location={{@model.currentLocation}} @saveTask={{this.saveLocationTask}} />
          {{/if}}
        {{/if}}

        {{#if (can "enable near-visit-registration")}}
          <SettingsPanel @isExpandable={{true}} @isOpen={{@model.currentLocation.nearVisitScreeningEnabled}} as |panel|>
            <panel.header
              data-test-near-visit-screening-heading
              @iconUri="/assets/images/features/clock.svg"
              @title="Near visit registration"
              @showCancel={{false}}
              @description="Visitors can only register within <strong>24 hours</strong> of their visit."
              as |header|
            >
              <header.controls>
                <span class="inline-block relative">
                  <SlideToggle
                    data-test-near-visit-screening-toggle
                    @isChecked={{@model.currentLocation.nearVisitScreeningEnabled}}
                    @onClick={{perform this.toggleNearVisitScreeningEnabled}}
                    @isDisabled={{this.isNearVisitDisabledByReminders}}
                  />
                  {{#if this.isNearVisitDisabledByReminders}}
                    <EmberTooltip @side="top" @text="Visitor reminder emails are enabled." />
                  {{/if}}
                </span>
              </header.controls>
            </panel.header>
            <panel.body as |SPContent|>
              <SPContent @style="inset">
                <MessageBox
                  @type="warning"
                  @header="Test result documents might be impacted"
                  class="mt-4"
                  data-test-covid-19-documents-impacted
                >
                  <p class="mb-0">
                    Test result documents is activated, and turning this feature on might affect the time window of when
                    visitors can upload their documents before their entry and admin approval. Learn more about
                    <a
                      href="https://envoy.help/en/articles/6093832-proof-of-vaccination-test-result-tracking-for-visitors"
                      target="_blank"
                      rel="noopener"
                    >managing health&nbsp;documents</a>.
                  </p>
                </MessageBox>
              </SPContent>
            </panel.body>
          </SettingsPanel>
        {{/if}}

        {{#if (can "enable front-desk-notifications")}}
          <SettingsPanel as |panel|>
            <panel.header
              @iconUri="/assets/images/features/notifications.svg"
              @title="Invite notifications"
              as |header|
            >
              <header.description>
                {{#let "front desk admin" as |linkText|}}
                  <span>Email the</span>
                  {{#if (can "visit-local-admins manage-settings")}}
                    <LinkTo @route="manage.admin-users" tabindex="-1">{{linkText}}</LinkTo>
                  {{else}}
                    <span>{{linkText}}</span>
                  {{/if}}
                  <span>every time an employee invites a visitor.</span>
                {{/let}}
              </header.description>
              <header.controls>
                <label class="toggle">
                  <input
                    checked={{@model.currentLocation.ccReceptionistEnabled}}
                    data-test-notify-receptionist-toggle
                    disabled={{this.saveLocationTask.isRunning}}
                    type="checkbox"
                    {{on "change" (perform this.toggleCCReceptionistEnabled value="target.checked")}}
                  />
                  <span></span>
                </label>
              </header.controls>
            </panel.header>
          </SettingsPanel>
        {{/if}}

        {{#if (can "customize email visitor-email")}}
          <MessageBox @type="attention" @header="Looking for invitation emails?" class="my-4">
            <p class="mb-0">
              Invitation emails have moved. To customize invitation emails,
              <LinkTo
                data-test-link-to-email-templates
                @route="visitors.settings.email-templates"
                @query={{hash utm_campaign="dlv-meet-me" utm_source="settings"}}
              >
                go to email templates &#8594;
              </LinkTo>
            </p>
          </MessageBox>
        {{else if (or (can "customize any external-notifications") this.showPropertyQrCode)}}
          <SettingsPanel
            @isOpen={{can "customize email for external-notifications"}}
            data-test-customize-visitor-email
            as |panel|
          >
            <panel.header
              @iconUri="/assets/images/features/invite.svg"
              @showCancel={{false}}
              @title="Visitor registration email template"
              as |header|
            >
              <header.description>
                <p class="m-0">Customize the email that visitors receive to remind them of their visit.</p>

                {{#if this.showPropertyQrCode}}
                  <MessageBox @type="info" class="mt-2" data-test-property-qr-code-message>
                    <b>Invite QR code includes property access pass</b>
                    <p class="m-0">Learn more about
                      <a
                        href="http://envoy.help/en/articles/8725726-digital-passes-for-property-visitors"
                        target="_blank"
                        rel="noopener"
                      >property passes for approved visitors.</a></p>
                  </MessageBox>
                {{/if}}
              </header.description>
              {{#if (cannot "customize email for external-notifications")}}
                <header.controls>
                  <a class="btn btn--small btn--brand" href="javascript:void(0);" onclick="Intercom('show')">
                    Contact us
                  </a>
                </header.controls>
              {{/if}}
            </panel.header>
            <panel.bodyStates
              @isEditing={{this.editEmailVisible}}
              @toEditing={{fn (mut this.editEmailVisible) true}}
              @editLabel="Edit text"
              @isPreviewing={{not this.editEmailVisible}}
              @toPreviewing={{queue (fn (mut this.editEmailVisible) false) this.getEmailPreview}}
              @previewLabel="Preview email"
            />
            <panel.body as |SPContent|>
              <SPContent class="mt-4 mr-16">
                <p class="text-sm" style="max-width: 640px;">
                  Help your visitors prepare for their visit by including directions, information on when to arrive, and
                  anything they might need to bring along, like an ID.
                </p>
                {{#if this.editEmailVisible}}
                  <SuperTextEditor
                    {{! @glint-expect-error }}
                    class="ember-prose-mirror"
                    @value={{@model.currentLocation.preRegistrationNotes}}
                    @isRtl={{isRTLLocale @model.currentLocation.locale}}
                    @onContentChange={{this.preRegistrationNotesUpdated}}
                  />
                {{else}}
                  <div class="emailHtmlHeader">
                    <div class="macStopLights">
                      <span class="text-red-50"></span>
                      <span class="text-papaya-50"></span>
                      <span class="text-cilantro-50"></span>
                      <UiButton
                        class={{concat "sendTestPreRegistrationEmail" (if this.testEmailSent " sent")}}
                        aria-disabled={{this.sendTestEmailTask.isRunning}}
                        disabled={{this.sendTestEmailTask.isRunning}}
                        {{on "click" (perform this.sendTestEmailTask)}}
                      >
                        {{if this.sendTestEmailTask.isRunning "Sending..." "Send test to me"}}
                      </UiButton>
                    </div>
                    <table class="macMailHeader">
                      <tbody>
                        <tr>
                          <td class="leftSide">From:</td>
                          <td>Envoy <span class="address">&lt;no-reply@envoy.com&gt;</span></td>
                        </tr>
                        <tr>
                          <td class="leftSide">To:</td>
                          <td data-test-to-field>
                            {{@model.currentUser.fullName}}
                            <span class="address">&lt;{{@model.currentUser.email}}&gt;</span>
                          </td>
                        </tr>
                        <tr class="subject">
                          <td class="leftSide">Subject:</td>
                          <td>
                            <strong data-test-preview-subject>
                              {{#if this.previewEmailObject}}
                                {{this.previewEmailObject.subject}}
                              {{else}}
                                Loading...
                              {{/if}}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {{#if this.previewEmailObject}}
                    <iframe
                      title="Pre-Registration Preview"
                      class="emailHtml preRegistrationIframe"
                      srcdoc={{this.previewEmailObject.body}}
                    ></iframe>
                  {{else}}
                    <div class="emailHtml relative">
                      <LoadingSpinnerPulse />
                    </div>
                  {{/if}}
                {{/if}}
                <UiButton
                  class="my-8 btn btn--brand"
                  {{on "click" (perform this.saveLocationTask @model.currentLocation)}}
                >
                  {{if this.saveLocationTask.isRunning "Saving..." "Save email text"}}
                </UiButton>
              </SPContent>
            </panel.body>
          </SettingsPanel>
        {{/if}}

        <p class="text-sm mb-0">Learn more about
          <a
            href="https://help.envoy.com/about-pre-registration/"
            target="_blank"
            tabindex="-1"
            rel="noopener"
          >pre-registering visitors</a>.</p>
      </PageSection>

      {{#if (can "configure security-desk")}}
        <PageSection>
          <h3>Security desk link</h3>

          <SettingsPanel
            @isExpandable={{true}}
            @isOpen={{this.isSecurityDeskLinkPanelOpen}}
            @onCancel={{fn (mut this.isSecurityDeskLinkPanelOpen) false}}
            as |panel|
          >
            <panel.header
              @iconUri="/assets/images/monitor-security-desk.svg"
              @title="Security desk link"
              @description="Generate a link to a live list of the day's invited visitors."
              @onCancel={{fn (mut this.isSecurityDeskLinkPanelOpen) false}}
              as |header|
            >
              <header.controls>
                {{#if (and (not panel.isOpen) @model.currentLocation.securityDeskLinkEnabled)}}
                  <button
                    type="button"
                    {{on "click" (fn (mut this.isSecurityDeskLinkPanelOpen) true)}}
                    class="btn btn--small btn--brand"
                    data-test-edit-security-desk-link
                  >
                    Edit
                  </button>
                {{/if}}

                {{#if (and (not panel.isOpen) (not @model.currentLocation.securityDeskLinkEnabled))}}
                  <button
                    type="button"
                    {{on
                      "click"
                      (queue (perform this.enableSecurityDeskLink) (fn (mut this.isSecurityDeskLinkPanelOpen) true))
                    }}
                    class="btn btn--small btn--brand"
                    data-test-enable-security-desk-link
                  >
                    Enable
                  </button>
                {{/if}}
              </header.controls>
            </panel.header>

            <panel.body as |Content|>
              <Content @style="inset">
                <div class="max-w-xl">
                  <div class="my-8">
                    <MessageBox @type="warning">
                      Keep this link internal. Anyone with access can view your invited visitors.
                    </MessageBox>
                  </div>

                  <div class="mb-4" data-test-security-desk>
                    <a
                      class="input mb-0 max-w-xl text-carbon-70"
                      style="margin-bottom: 0;"
                      href={{@model.currentLocation.securityDeskLink}}
                      target="_blank"
                      tabindex="-1"
                      rel="noopener"
                    >
                      {{@model.currentLocation.securityDeskLink}}
                    </a>
                    <label>

                      <button
                        type="button"
                        class="-mt-2 btn-reset text-gem-50 font-normal"
                        {{on "click" (fn (mut this.confirmRegenerateSecurityDeskLink) true)}}
                      >
                        Regenerate
                      </button>

                      {{#if this.confirmRegenerateSecurityDeskLink}}
                        <ConfirmDirtyTransition
                          @headerMessage="Regenerate security desk link?"
                          @bodyMessage="Are you sure want to regenerate the security desk link? The existing link will stop working."
                          @confirmText="Regenerate link"
                          @confirm={{queue
                            this.regenerateSecurityDeskLink
                            (fn (mut this.confirmRegenerateSecurityDeskLink) false)
                          }}
                          @cancel={{fn (mut this.confirmRegenerateSecurityDeskLink) false}}
                        />
                      {{/if}}

                    </label>
                  </div>
                </div>

                {{#if @model.currentLocation.securityDeskLinkEnabled}}
                  <panel.footer>
                    <button
                      type="button"
                      {{on
                        "click"
                        (queue (perform this.disableSecurityDeskLink) (fn (mut this.isSecurityDeskLinkPanelOpen) false))
                      }}
                      class="btn btn--small btn--outlineFill"
                      data-test-disable-security-desk-link
                    >
                      Disable
                    </button>
                  </panel.footer>
                {{/if}}
              </Content>
            </panel.body>
          </SettingsPanel>

          <p class="text-sm mb-0">Learn more about the
            <a
              href="https://help.envoy.com/about-workplace-security/#security-desk-link"
              target="_blank"
              tabindex="-1"
              rel="noopener"
            >security desk link</a>.</p>
        </PageSection>
      {{/if}}
    </UiPage>
  </template>
}

export default RouteTemplate(VisitorsSettingsInvitesTemplate);
